<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { ArrowLeft, BookTemplate, FileStack, Pencil } from '@lucide/vue';
import { useAuthStore } from '@/stores/auth';
import { useInvalidation } from '@/composables/useInvalidation';
import { useActiveTab } from '@/composables/useActiveTab';
import { useActiveTabScroll } from '@/composables/useActiveTabScroll';
import StatoErrore from '@/components/ui/StatoErrore.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import type { Template } from '@evolfin/shared';
import FormTemplate from '../components/FormTemplate.vue';
import AnagraficaTemplate from '../components/AnagraficaTemplate.vue';
import EditorStruttura from '../components/EditorStruttura.vue';
import LinkTemplate from '../components/LinkTemplate.vue';
import { dettaglioTemplate } from '../api';

definePage({ meta: { titolo: 'Template', privilegi: ['can_manage_templates'] } });

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const invalidate = useInvalidation('template');

const templateId = computed(() => Number((route.params as { id: string }).id));
const puoGestire = computed(() => auth.ha('can_manage_templates'));

const {
  data: template,
  isLoading,
  isError,
  refetch,
} = useQuery({
  queryKey: ['template', templateId],
  queryFn: () => dettaglioTemplate(templateId.value),
  // È la pagina su cui si lavora: rimontandola si riparte dal server, non da
  // una copia di trenta secondi fa.
  refetchOnMount: 'always',
});

const tabs = [
  { chiave: 'dati', etichetta: 'Dati', icona: BookTemplate },
  { chiave: 'struttura', etichetta: 'Struttura', icona: FileStack },
] as const;
type ChiaveTab = (typeof tabs)[number]['chiave'];
const tab = useActiveTab(
  tabs.map((t) => t.chiave),
  'dati',
);
useActiveTabScroll(tab);

// Modifica dati base in-place sulla scheda (niente modale), come per Clienti/Partner.
const modifica = ref(false);
function avviaModifica(): void {
  tab.value = 'dati';
  modifica.value = true;
}
function selezionaTab(chiave: ChiaveTab): void {
  modifica.value = false;
  tab.value = chiave;
}
function fineModifica(_template: Template): void {
  modifica.value = false;
  void invalidate();
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <button
      type="button"
      class="flex w-fit items-center gap-1.5 text-[13px] text-ink-secondary transition-colors hover:text-ink"
      @click="router.push('/template')"
    >
      <ArrowLeft :size="15" :stroke-width="1.9" /> Template
    </button>

    <div v-if="isLoading" class="text-[13px] text-ink-muted">Caricamento…</div>

    <StatoErrore
      v-else-if="isError || !template"
      titolo="Template non trovato"
      messaggio="Il template richiesto non esiste o non è più disponibile."
      @riprova="refetch()"
    />

    <template v-else>
      <header class="flex items-start justify-between gap-4">
        <div class="flex items-start gap-3">
          <span
            class="grid size-12 shrink-0 place-items-center rounded-lg bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
            aria-hidden="true"
          >
            <BookTemplate :size="24" :stroke-width="1.8" />
          </span>
          <div>
            <div class="flex items-center gap-2.5">
              <h1 class="font-display text-[28px] leading-tight font-semibold">
                {{ template.nome }}
              </h1>
              <span
                v-if="!template.attivo"
                class="rounded-pill px-2 py-0.5 text-[11px] font-medium"
                :style="{
                  background: 'color-mix(in oklch, var(--color-text-muted) 14%, transparent)',
                  color: 'var(--color-text-secondary)',
                }"
              >
                Disattivato
              </span>
            </div>
            <p v-if="template.descrizione" class="mt-0.5 text-[13px] text-ink-secondary">
              {{ template.descrizione }}
            </p>
          </div>
        </div>
        <PulsanteAzione v-if="puoGestire && !modifica" variante="contorno" @click="avviaModifica">
          Modifica
          <template #icona><Pencil :size="15" :stroke-width="1.9" /></template>
        </PulsanteAzione>
      </header>

      <nav
        ref="tabBar"
        class="flex gap-1 overflow-x-auto border-b border-line-soft"
        aria-label="Sezioni template"
      >
        <button
          v-for="t in tabs"
          :key="t.chiave"
          type="button"
          class="flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors"
          :aria-current="tab === t.chiave ? 'true' : undefined"
          :class="
            tab === t.chiave
              ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
              : 'border-transparent text-ink-secondary hover:text-ink'
          "
          @click="selezionaTab(t.chiave)"
        >
          <component :is="t.icona" :size="15" :stroke-width="1.8" />
          {{ t.etichetta }}
        </button>
      </nav>

      <section>
        <template v-if="tab === 'dati'">
          <FormTemplate
            v-if="modifica"
            :template="template"
            @salvato="fineModifica"
            @annulla="modifica = false"
          />
          <div v-else class="flex flex-col gap-4">
            <AnagraficaTemplate :template="template" />
            <LinkTemplate :template-id="template.id" class="mx-auto w-full max-w-2xl" />
          </div>
        </template>
        <EditorStruttura
          v-else-if="tab === 'struttura'"
          :template-id="template.id"
          :fasi="template.fasi"
          :documenti-generali="template.documentiGenerali"
          @salvato="invalidate()"
        />
      </section>
    </template>
  </div>
</template>
